import React from 'react';
import {
  PAGE_SIZE_OPTIONS,
  type PageSizeOption,
  type PaginationController,
} from '../../hooks/usePagination';

export interface PaginationBarProps extends Partial<PaginationController> {
  /** Pass the controller returned by usePaginatedList or useServerPaginatedQuery */
  pagination?: PaginationController;
  /** Optional noun for rows, e.g. "projects", "licenses", "users" (default: "rows") */
  itemLabel?: string;
  /** Compact variant for modals or nested panels */
  compact?: boolean;
  /** Additional container className */
  className?: string;
}

export const PaginationBar: React.FC<PaginationBarProps> = ({
  pagination,
  page: pageProp,
  pageSize: pageSizeProp,
  totalItems: totalItemsProp,
  totalPages: totalPagesProp,
  startIndex: startIndexProp,
  endIndex: endIndexProp,
  prefetchedPagesReady: prefetchedReadyProp,
  prefetching: prefetchingProp,
  setPage: setPageProp,
  setPageSize: setPageSizeProp,
  itemLabel = 'rows',
  compact = false,
  className = '',
}) => {
  const page = pagination?.page ?? pageProp ?? 0;
  const pageSize = pagination?.pageSize ?? pageSizeProp ?? 10;
  const totalItems = pagination?.totalItems ?? totalItemsProp ?? 0;
  const totalPages =
    pagination?.totalPages ?? totalPagesProp ?? Math.max(1, Math.ceil(totalItems / pageSize));
  const startIndex =
    pagination?.startIndex ??
    startIndexProp ??
    (totalItems === 0 ? 0 : page * pageSize + 1);
  const endIndex =
    pagination?.endIndex ??
    endIndexProp ??
    (totalItems === 0 ? 0 : Math.min(totalItems, (page + 1) * pageSize));
  const prefetchedPagesReady = pagination?.prefetchedPagesReady ?? prefetchedReadyProp ?? 0;
  const prefetching = pagination?.prefetching ?? prefetchingProp ?? false;
  const setPage = pagination?.setPage ?? setPageProp ?? (() => {});
  const setPageSize = pagination?.setPageSize ?? setPageSizeProp ?? (() => {});

  // Build windowed page numbers around the active page
  const pageNumbers = React.useMemo(() => {
    const pages: (number | 'ellipsis-start' | 'ellipsis-end')[] = [];
    if (totalPages <= 7) {
      for (let i = 0; i < totalPages; i++) pages.push(i);
      return pages;
    }

    pages.push(0);
    const windowStart = Math.max(1, page - 1);
    const windowEnd = Math.min(totalPages - 2, page + 1);

    if (windowStart > 1) {
      pages.push('ellipsis-start');
    }
    for (let i = windowStart; i <= windowEnd; i++) {
      pages.push(i);
    }
    if (windowEnd < totalPages - 2) {
      pages.push('ellipsis-end');
    }
    pages.push(totalPages - 1);
    return pages;
  }, [page, totalPages]);

  return (
    <nav
      aria-label={`Pagination for ${itemLabel}`}
      className={`flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 bg-slate-50/70 ${
        compact ? 'px-3 py-2 text-xs' : 'px-4 py-3 text-xs'
      } text-slate-600 ${className}`}
    >
      {/* Left: Range summary + lookahead prefetch status */}
      <div className="flex flex-wrap items-center gap-2.5 tabular-nums">
        <span>
          {totalItems === 0 ? (
            <>No {itemLabel}</>
          ) : (
            <>
              Showing <span className="font-semibold text-slate-900">{startIndex.toLocaleString()}</span>
              {'–'}
              <span className="font-semibold text-slate-900">{endIndex.toLocaleString()}</span> of{' '}
              <span className="font-semibold text-slate-900">{totalItems.toLocaleString()}</span>{' '}
              {itemLabel}
            </>
          )}
        </span>
        {prefetchedPagesReady > 0 && (
          <span
            title={`Next ${prefetchedPagesReady} page${prefetchedPagesReady > 1 ? 's' : ''} prefetched in memory for instant navigation`}
            className="hidden sm:inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700 border border-emerald-200/70"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
            +{prefetchedPagesReady}p prefetched
          </span>
        )}
        {prefetching && prefetchedPagesReady === 0 && (
          <span className="hidden sm:inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-medium text-indigo-600 border border-indigo-200/70">
            Prefetching…
          </span>
        )}
      </div>

      {/* Right: Page size selector (10, 20, 50) + Page navigation buttons */}
      <div className="flex flex-wrap items-center gap-3">
        <label className="inline-flex items-center gap-1.5 text-xs text-slate-600">
          <span className="text-slate-500">Rows:</span>
          <select
            aria-label={`Rows per page for ${itemLabel}`}
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value) as PageSizeOption)}
            className="rounded-lg border border-slate-300 bg-white px-2 py-1 text-xs font-medium text-slate-800 shadow-2xs focus:border-indigo-500 focus:outline-none tabular-nums"
          >
            {PAGE_SIZE_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </label>

        <div className="inline-flex items-center gap-1 tabular-nums">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page <= 0}
            aria-label="Previous page"
            className="inline-flex items-center rounded-lg border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-45 transition-colors cursor-pointer"
          >
            ← Prev
          </button>

          {!compact && (
            <div className="hidden md:inline-flex items-center gap-1">
              {pageNumbers.map((item) =>
                typeof item === 'string' ? (
                  <span key={item} className="px-1 text-slate-400 select-none">
                    …
                  </span>
                ) : (
                  <button
                    key={item}
                    type="button"
                    onClick={() => setPage(item)}
                    aria-label={`Page ${item + 1}`}
                    aria-current={item === page ? 'page' : undefined}
                    className={`min-w-[1.75rem] rounded-lg px-2 py-1 text-xs font-semibold transition-colors cursor-pointer ${
                      item === page
                        ? 'bg-indigo-600 text-white shadow-2xs'
                        : 'border border-slate-200 bg-white text-slate-700 hover:bg-slate-100'
                    }`}
                  >
                    {item + 1}
                  </button>
                ),
              )}
            </div>
          )}

          {compact && (
            <span className="px-2 text-xs font-medium text-slate-600">
              {page + 1} / {totalPages}
            </span>
          )}

          <button
            type="button"
            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            disabled={page + 1 >= totalPages}
            aria-label="Next page"
            className="inline-flex items-center rounded-lg border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-45 transition-colors cursor-pointer"
          >
            Next →
          </button>
        </div>
      </div>
    </nav>
  );
};
